// src/pages/Categorias.js
//
// Catálogo unificado de categorías (mig 038):
//  - Globales: sembradas por tipo de negocio (market/fruver/drogueria...).
//    Solo se pueden ver, no editar/eliminar. Chip gris "Estándar".
//  - Propias de la empresa: creadas por el tendero. Chip azul, editables.
//
// Estructura padre → hija: los padres se listan primero, cada uno expande
// para mostrar sus subcategorías. Se pueden crear categorías raíz o hijas.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, Button, IconButton,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Tooltip, Collapse, Divider, TextField as TF,
} from '@mui/material';
import CategoryIcon      from '@mui/icons-material/Category';
import AddIcon           from '@mui/icons-material/Add';
import EditIcon          from '@mui/icons-material/Edit';
import DeleteIcon        from '@mui/icons-material/Delete';
import ExpandMoreIcon    from '@mui/icons-material/ExpandMore';
import ExpandLessIcon    from '@mui/icons-material/ExpandLess';
import LockIcon          from '@mui/icons-material/Lock';
import { useApp } from '../context/AppContext';
import { categoriasCatalogo as apiCats } from '../api/posApi';

const Categorias = () => {
  const { empresa, notify } = useApp();
  const [items, setItems]         = useState([]);
  const [cargando, setCargando]   = useState(false);
  const [expandidas, setExpand]   = useState({});
  const [dialog, setDialog]       = useState(null); // { modo: 'crear'|'editar', cat?, padreId? }

  const empresaId = empresa?.empresaId || empresa?.EmpresaId;

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setCargando(true);
    try {
      const r = await apiCats.listar(empresaId, false); // incluye inactivas
      setItems(Array.isArray(r) ? r : []);
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudieron cargar las categorías.', 'error');
    } finally { setCargando(false); }
  }, [empresaId, notify]);

  useEffect(() => { cargar(); }, [cargar]);

  const padres = items.filter(c => !c.categoriaPadreId);
  const hijasDe = (id) => items.filter(c => c.categoriaPadreId === id);
  const toggle = (id) => setExpand(e => ({ ...e, [id]: !e[id] }));

  const eliminar = async (cat) => {
    if (!window.confirm(`¿Eliminar "${cat.nombre}"?`)) return;
    try {
      const r = await apiCats.eliminar(cat.categoriaCatalogoId);
      notify(r?.message || 'Eliminada.', 'success');
      await cargar();
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudo eliminar.', 'error');
    }
  };

  if (!empresa) {
    return <Alert severity="info">Selecciona una empresa desde el header.</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center"
             sx={{ mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <CategoryIcon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h4" fontWeight={700}>Categorías</Typography>
            <Typography variant="body2" color="text.secondary">
              Catálogo estándar del tipo de negocio + tus propias
            </Typography>
          </Box>
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />}
                onClick={() => setDialog({ modo: 'crear' })}>
          Nueva categoría
        </Button>
      </Stack>

      <Alert severity="info" variant="outlined" sx={{ mb: 2 }} icon={<LockIcon />}>
        Las categorías del <b>catálogo estándar</b> están compartidas por todos los
        negocios de tu tipo. Se pueden usar pero no editar. Tus categorías propias
        (azul) sí puedes editarlas y eliminarlas.
      </Alert>

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : padres.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <CategoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            Aún no hay categorías. Crea la primera.
          </Typography>
        </Card>
      ) : (
        <Stack spacing={1}>
          {padres.map((padre) => {
            const hijas = hijasDe(padre.categoriaCatalogoId);
            const abierto = expandidas[padre.categoriaCatalogoId];
            return (
              <Card key={padre.categoriaCatalogoId} variant="outlined"
                    sx={{ borderColor: padre.esPropia ? 'primary.main' : 'divider',
                          opacity: padre.activa ? 1 : 0.5 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton size="small" onClick={() => toggle(padre.categoriaCatalogoId)}
                                disabled={hijas.length === 0}>
                      {abierto ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <Box sx={{ flexGrow: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography fontWeight={600}>{padre.nombre}</Typography>
                        {padre.esPropia ? (
                          <Chip size="small" label="Propia" color="primary" variant="outlined" />
                        ) : (
                          <Chip size="small" label="Estándar" icon={<LockIcon sx={{ fontSize: 14 }} />} />
                        )}
                        {!padre.activa && <Chip size="small" label="Inactiva" color="default" />}
                        {hijas.length > 0 && (
                          <Chip size="small" label={`${hijas.length} sub`} variant="outlined" />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {padre.codigo}
                      </Typography>
                    </Box>
                    <Stack direction="row">
                      <Tooltip title="Agregar subcategoría">
                        <IconButton size="small"
                                    onClick={() => setDialog({ modo: 'crear', padreId: padre.categoriaCatalogoId })}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {padre.esPropia && (
                        <>
                          <Tooltip title="Editar">
                            <IconButton size="small"
                                        onClick={() => setDialog({ modo: 'editar', cat: padre })}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton size="small" color="error"
                                        onClick={() => eliminar(padre)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </Stack>

                  <Collapse in={abierto}>
                    <Stack spacing={0.5} sx={{ mt: 1, ml: 5, pl: 1, borderLeft: 2, borderColor: 'divider' }}>
                      {hijas.map((hija) => (
                        <Stack key={hija.categoriaCatalogoId}
                               direction="row" alignItems="center" spacing={1}
                               sx={{ py: 0.75, opacity: hija.activa ? 1 : 0.5 }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="body2">{hija.nombre}</Typography>
                              {hija.esPropia
                                ? <Chip size="small" label="Propia" color="primary" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                                : <Chip size="small" label="Estándar" sx={{ height: 20, fontSize: 10 }} />}
                              {!hija.activa && <Chip size="small" label="Inactiva" sx={{ height: 20, fontSize: 10 }} />}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">{hija.codigo}</Typography>
                          </Box>
                          {hija.esPropia && (
                            <Stack direction="row">
                              <IconButton size="small"
                                          onClick={() => setDialog({ modo: 'editar', cat: hija })}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error"
                                          onClick={() => eliminar(hija)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {dialog && (
        <DialogCategoria
          empresaId={empresaId}
          padres={padres}
          modo={dialog.modo}
          cat={dialog.cat}
          padreIdSugerido={dialog.padreId}
          onClose={() => setDialog(null)}
          onSaved={async (msg) => { setDialog(null); await cargar(); notify(msg, 'success'); }}
          onError={(m) => notify(m, 'error')}
        />
      )}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
// Dialog Crear/Editar
// ═══════════════════════════════════════════════════════════════

function DialogCategoria({ empresaId, padres, modo, cat, padreIdSugerido, onClose, onSaved, onError }) {
  const editando = modo === 'editar';
  const [nombre, setNombre]     = useState(cat?.nombre || '');
  const [padreId, setPadreId]   = useState(cat?.categoriaPadreId || padreIdSugerido || 0);
  const [activa, setActiva]     = useState(cat?.activa ?? true);
  const [guardando, setGuardando] = useState(false);

  const submit = async () => {
    if (!nombre.trim()) return;
    setGuardando(true);
    try {
      if (editando) {
        await apiCats.actualizar(cat.categoriaCatalogoId, {
          nombre: nombre.trim(),
          categoriaPadreId: padreId || 0,
          activa,
        });
        onSaved('Categoría actualizada.');
      } else {
        await apiCats.crear({
          empresaId,
          nombre: nombre.trim(),
          categoriaPadreId: padreId || null,
        });
        onSaved('Categoría creada.');
      }
    } catch (e) {
      onError(e.mensajeUsuario || 'No se pudo guardar.');
    } finally { setGuardando(false); }
  };

  return (
    <Dialog open onClose={() => !guardando && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>{editando ? 'Editar categoría' : 'Nueva categoría'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Nombre" fullWidth autoFocus required
                     value={nombre} onChange={(e) => setNombre(e.target.value)} />
          <TextField select label="Categoría padre (opcional)" fullWidth
                     value={padreId} onChange={(e) => setPadreId(Number(e.target.value))}
                     helperText="Déjala vacía si es una categoría de primer nivel.">
            <MenuItem value={0}>(sin padre — nivel principal)</MenuItem>
            {padres.map(p => (
              <MenuItem key={p.categoriaCatalogoId} value={p.categoriaCatalogoId}>
                {p.nombre}
              </MenuItem>
            ))}
          </TextField>
          {editando && (
            <TextField select label="Estado" fullWidth value={activa ? '1' : '0'}
                       onChange={(e) => setActiva(e.target.value === '1')}>
              <MenuItem value="1">Activa</MenuItem>
              <MenuItem value="0">Inactiva</MenuItem>
            </TextField>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button variant="contained" onClick={submit}
                disabled={!nombre.trim() || guardando}>
          {guardando ? 'Guardando…' : editando ? 'Guardar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Categorias;
